import type { AppContext } from '@application/context/AppContext';
import {
  HybridBackupConfigService,
} from '@application/backup/HybridBackupConfigService';
import { HybridVaultService } from '@application/backup/HybridVaultService';
import { startHybridVaultScheduler } from '@application/backup/HybridVaultScheduler';
import { enforceHybridConsentGate } from '@application/legal/HybridConsentGate';
import '@presentation/components/Modal';
import { ToastService } from '@presentation/components/Toast';
import { openHybridBackupSetupWizard } from './HybridBackupSetupWizard';
import { mountHybridBackupStatusBanner } from './HybridBackupStatusBanner';

type NexxusModal = HTMLElement & { open: () => void; close: () => void };

/**
 * Confirme une clé déjà affichée (état `recovery_key_unconfirmed`) sans la réafficher.
 * Propose aussi de relancer l’assistant si la clé a été perdue.
 */
export function openHybridPendingRecoveryConfirm(options?: {
  onCompleted?: () => void;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const modal = document.createElement('nexxus-modal') as NexxusModal;
    modal.setAttribute('title', 'Confirmer la clé de récupération');
    modal.setAttribute('size', 'sm');

    const body = document.createElement('div');
    body.className = 'modal-body';
    body.innerHTML = `
      <p style="margin:0 0 14px;line-height:1.5;color:var(--gris,#586069);">
        La clé de récupération a déjà été affichée une seule fois. Si vous l’avez conservée en lieu sûr,
        confirmez ci-dessous pour activer la sauvegarde chiffrée.
      </p>
      <div class="field" style="display:flex;align-items:flex-start;gap:10px;">
        <input type="checkbox" id="hybrid-pending-key-stored" aria-describedby="hybrid-pending-key-hint">
        <div>
          <label for="hybrid-pending-key-stored" style="font-weight:600;cursor:pointer;">
            Je l'ai conservée en lieu sûr
          </label>
          <span id="hybrid-pending-key-hint" class="hint" style="display:block;margin-top:4px;">
            Obligatoire. Sans cette confirmation, aucun envoi chiffré ne part.
          </span>
        </div>
      </div>
      <p id="hybrid-pending-key-error" role="alert" style="display:none;margin:12px 0 0;color:var(--rouge,#b42318);font-size:0.9rem;"></p>
      <div style="display:flex;flex-wrap:wrap;justify-content:flex-end;gap:10px;margin-top:22px;">
        <button type="button" class="btn btn-ghost" data-restart>Relancer l’assistant</button>
        <button type="button" class="btn btn-ghost" data-cancel>Plus tard</button>
        <button type="button" class="btn btn-primary" data-confirm disabled>Confirmer</button>
      </div>
    `;
    modal.appendChild(body);
    document.body.appendChild(modal);

    const checkbox = body.querySelector<HTMLInputElement>('#hybrid-pending-key-stored')!;
    const errorEl = body.querySelector<HTMLElement>('#hybrid-pending-key-error')!;
    const confirmBtn = body.querySelector<HTMLButtonElement>('[data-confirm]')!;
    let settled = false;

    const finish = (ok: boolean): void => {
      if (settled) return;
      settled = true;
      modal.close();
      modal.remove();
      if (ok) options?.onCompleted?.();
      resolve(ok);
    };

    checkbox.addEventListener('change', () => {
      confirmBtn.disabled = !checkbox.checked;
      errorEl.style.display = 'none';
      errorEl.textContent = '';
    });

    body.querySelector('[data-cancel]')?.addEventListener('click', () => finish(false));
    modal.addEventListener('close', () => finish(false), { once: true });

    body.querySelector('[data-restart]')?.addEventListener('click', () => {
      void (async () => {
        try {
          await HybridBackupConfigService.cancelPending();
        } catch {
          /* best-effort */
        }
        finish(false);
        await openHybridBackupSetupWizard({ onCompleted: options?.onCompleted });
      })();
    });

    confirmBtn.addEventListener('click', () => {
      if (!checkbox.checked) {
        errorEl.style.display = 'block';
        errorEl.textContent = 'Cochez la case pour confirmer que vous avez conservé la clé.';
        checkbox.setAttribute('aria-invalid', 'true');
        checkbox.focus();
        return;
      }
      confirmBtn.disabled = true;
      void HybridBackupConfigService.confirmRecoveryKeyStored()
        .then(() => {
          ToastService.success('La sauvegarde chiffrée est active.');
          finish(true);
        })
        .catch((err: unknown) => {
          confirmBtn.disabled = false;
          errorEl.style.display = 'block';
          errorEl.textContent =
            err instanceof Error ? err.message : 'Confirmation impossible. Réessayez.';
        });
    });

    modal.open();
    requestAnimationFrame(() => checkbox.focus());
  });
}

export interface MountHybridBackupUiOptions {
  ctx: AppContext;
  tenantId: string;
  root: HTMLElement;
  /** Conteneur bannière (créé si absent). */
  bannerHost?: HTMLElement | null;
}

/**
 * Point d’entrée Hybride après connexion : consentement, planificateur, bannière, assistant si besoin.
 */
export async function mountHybridBackupUi(opts: MountHybridBackupUiOptions): Promise<() => void> {
  const { ctx, tenantId, root } = opts;
  if (!HybridVaultService.isEnabled(ctx)) {
    return () => undefined;
  }

  const consented = await enforceHybridConsentGate(root, tenantId);
  if (consented) {
    startHybridVaultScheduler(ctx, tenantId);
  }

  let bannerHost = opts.bannerHost ?? root.querySelector<HTMLElement>('#hybrid-backup-status-banner');
  if (!bannerHost) {
    bannerHost = document.createElement('div');
    bannerHost.id = 'hybrid-backup-status-banner';
    bannerHost.style.cssText = 'padding:12px 16px 0;';
    const main = root.querySelector('main.main');
    if (main) {
      main.insertBefore(bannerHost, main.firstChild);
    } else {
      root.appendChild(bannerHost);
    }
  }

  const refreshAfterSetup = (): void => {
    void HybridBackupConfigService.announceState();
  };

  const unmountBanner = mountHybridBackupStatusBanner({
    ctx,
    tenantId,
    host: bannerHost,
    onConfigure: () => {
      void openHybridBackupSetupWizard({ onCompleted: refreshAfterSetup });
    },
    onConfirmKey: () => {
      void openHybridPendingRecoveryConfirm({ onCompleted: refreshAfterSetup });
    },
  });

  if (consented) {
    const snapshot = await HybridBackupConfigService.getSetupSnapshot();
    if (snapshot.state === 'setup_required') {
      void openHybridBackupSetupWizard({ onCompleted: refreshAfterSetup });
    } else if (snapshot.state === 'recovery_key_unconfirmed' && !snapshot.replacementPending) {
      void openHybridPendingRecoveryConfirm({ onCompleted: refreshAfterSetup });
    }
  }

  return () => {
    unmountBanner();
  };
}
